import {
    ApolloClient,
    ApolloLink,
    CombinedGraphQLErrors,
    HttpLink,
    InMemoryCache
} from '@apollo/client';
import {ServerError} from '@apollo/client/errors';
import {catchError, defer, map, switchMap, throwError} from 'rxjs';
import {SetContextLink} from '@apollo/client/link/context';
import {
    getValidAccessToken,
    isSessionActive,
    logout as logoutSession,
    SessionError,
    SESSION_LOGOUT_REASONS
} from '@/services/auth/sessionService.js';

export {getValidAccessToken};

// Polyfill pour Promise.withResolvers si nécessaire (ES2024)
if (!Promise.withResolvers) {
    Promise.withResolvers = function () {
        let resolve, reject;
        const promise = new Promise((res, rej) => {
            resolve = res;
            reject = rej;
        });
        return { promise, resolve, reject };
    };
}

// Link HTTP
const httpLink = new HttpLink({
    uri: window.ENV.BACKEND_URL + '/api/graphql'
});

// Link de debug pour logger les requêtes
const debugLink = new ApolloLink((operation, forward) => {
    if (window.ENV.DEBUG === 'true' || window.ENV.DEBUG === true) {
        console.log(`[GraphQL Request]: ${operation.operationName}`);
    }
    return forward(operation);
});

const multipartHttpLink = new SetContextLink((prevContext, operation) => {
    const isSubscription =
        operation.query.definitions.some(
            (definition) =>
                definition.kind === 'OperationDefinition' &&
                definition.operation === 'subscription'
        );

    return {
        headers: {
            ...prevContext.headers,
            Accept: isSubscription
                ? 'multipart/mixed; subscriptionSpec=1.0, application/json'
                : 'application/json',
        },
    };
});

const is401Unauthorized = (error) => {
    if (!error) return false;
    if (CombinedGraphQLErrors.is(error)) {
        return error.errors.some(
            ({ message, extensions }) =>
                message === '401: Unauthorized' ||
                extensions?.code === 'UNAUTHENTICATED' ||
                extensions?.status === 401 ||
                extensions?.statusCode === 401
        );
    }
    return error.statusCode === 401 || error.networkError?.statusCode === 401 || error.status === 401;
};

const authLink = new ApolloLink((operation, forward) => {
    const send = (forceRefresh = false) => defer(() => getValidAccessToken(forceRefresh)).pipe(
        switchMap((token) => {
            if (!isSessionActive()) throw new SessionError(SESSION_LOGOUT_REASONS.TOKEN_UNAVAILABLE);
            const headers = Object.fromEntries(
                Object.entries(operation.getContext().headers ?? {})
                    .filter(([key]) => key.toLowerCase() !== 'authorization')
            );
            operation.setContext({ headers: { ...headers, authorization: `Bearer ${token}` } });
            return forward(operation);
        }),
        map((result) => {
            if (!isSessionActive()) throw new SessionError(SESSION_LOGOUT_REASONS.TOKEN_UNAVAILABLE);
            if (result.errors?.length) {
                const error = new CombinedGraphQLErrors(result);
                if (is401Unauthorized(error)) throw error;
            }
            return result;
        })
    );

    return send().pipe(catchError((error) => {
        if (!is401Unauthorized(error)) return throwError(() => error);
        const canReplay = operation.getContext().retryOnUnauthorized ?? (operation.operationType === 'query');
        if (!canReplay) {
            return defer(() => getValidAccessToken(true)).pipe(
                switchMap(() => throwError(() => error))
            );
        }
        return send(true).pipe(catchError((retryError) => {
            if (is401Unauthorized(retryError)) {
                void logoutSession({ reason: SESSION_LOGOUT_REASONS.AUTHENTICATION_REJECTED });
            }
            return throwError(() => retryError);
        }));
    }));
});

const client = new ApolloClient({
    link: ApolloLink.from([debugLink, authLink, multipartHttpLink, httpLink]),
    cache: new InMemoryCache(),
    defaultOptions: {
        watchQuery: {
            fetchPolicy: 'cache-and-network',
            errorPolicy: 'all'
        },
        query: {
            fetchPolicy: 'network-only',
            errorPolicy: 'all'
        }
    }
});

export default client;
